// GalleryViewNodeCard.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Utilities
import { getNodeSubtitle, isSubtitleFieldKey } from 'utilities/NodeSubtitle';
import { buildRichTextSummary } from 'utilities/RichText';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Hooks
import useGalleryCardLoadGate from 'views/gallery/hooks/useGalleryCardLoadGate';

// CSS
import './GalleryViewNodeCard.css';

/**
 * GalleryViewNodeCard
 *
 * Renders a gallery card for a generic node with cover, subtitle, and field rows.
 *
 * @param {array} cardFieldKeys - Field keys to show on the card.
 * @param {function} formatFieldValue - Formats a field value for display.
 * @param {object} node - Node rendered by the card.
 * @param {function} resolveFieldValue - Resolves a field value from the node.
 * @param {Map} schemaFieldMap - Schema lookup map for card fields.
 * @param {boolean} showCover - Whether to render the cover image.
 * @param {boolean} showSubtitle - Whether to render the subtitle block.
 * @param {boolean} showNoteContents - Whether note contents should be shown.
 * @param {object} theme - Theme tokens used for card styling.
 *
 * @returns {ReactNode} The rendered gallery node card.
 */
const GalleryViewNodeCard = ({
    node,
    cardFieldKeys,
    schemaFieldMap,
    resolveFieldValue,
    formatFieldValue,
    showCover = true,
    showSubtitle = true,
    showNoteContents = true,
    theme,
}) => {

    // ----- STATE ----- //

    const [coverImageUrl, setCoverImageUrl] = useState('');
    const [isCoverLoading, setIsCoverLoading] = useState(false);

    const rawCover = typeof node?.coverUrl === 'string' ? node.coverUrl.trim() : '';
    const storagePath = node?.coverStoragePath || null;
    const hasCoverSurface = showCover && Boolean(rawCover || storagePath);
    const coverLoadKey = `${node?.key || ''}:${rawCover}:${storagePath || ''}:${showCover ? '1' : '0'}`;

    // ----- HOOKS ----- //

    const { containerRef, shouldLoad } = useGalleryCardLoadGate(coverLoadKey);

    // ----- MEMOS ----- //

    const displayFields = useMemo(() => {
        if (!node || !Array.isArray(cardFieldKeys) || cardFieldKeys.length === 0) return [];

        const hasContentsField = schemaFieldMap.has('contents');
        return cardFieldKeys
            .map((fieldKey) => {
                if (showNoteContents && hasContentsField && fieldKey === 'contents') return null;

                const fieldDef = schemaFieldMap.get(fieldKey) || null;
                const fieldType = String(fieldDef?.type || '').toLowerCase();
                const hideLabel = isSubtitleFieldKey(fieldKey, fieldType);
                const fallbackLabel = fieldKey === 'dateCreated'
                    ? 'Created'
                    : (fieldKey === 'dateModified' ? 'Modified' : fieldKey);
                const label = fieldDef?.label || fieldDef?.name || fallbackLabel;
                const rawValue = fieldDef
                    ? resolveFieldValue(node, fieldDef)
                    : node?.[fieldKey];
                const displayValue = formatFieldValue(rawValue, fieldDef, fieldKey);
                if (!displayValue) return null;
                return {
                    fieldKey,
                    hideLabel,
                    label,
                    value: displayValue,
                };
            })
            .filter(Boolean);
    }, [node, cardFieldKeys, schemaFieldMap, resolveFieldValue, formatFieldValue, showNoteContents]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        let cancelled = false;

        if (!hasCoverSurface || !shouldLoad) {
            setCoverImageUrl('');
            setIsCoverLoading(false);
            return () => {
                cancelled = true;
            };
        }

        setCoverImageUrl('');
        setIsCoverLoading(true);

        (async () => {
            try {
                const resolved = await resolveImageUrl({
                    nodeKey: node?.key || null,
                    url: rawCover || null,
                    storagePath,
                });

                const finalUrl = (typeof resolved === 'string' && resolved.trim())
                    ? resolved.trim()
                    : rawCover;

                if (!finalUrl) {
                    if (!cancelled) {
                        setCoverImageUrl('');
                        setIsCoverLoading(false);
                    }
                    return;
                }

                const image = new Image();
                image.onload = () => {
                    if (cancelled) return;
                    setCoverImageUrl(finalUrl);
                    setIsCoverLoading(false);
                };
                image.onerror = () => {
                    if (cancelled) return;
                    setCoverImageUrl('');
                    setIsCoverLoading(false);
                };
                image.src = finalUrl;
            } catch (error) {
                if (!cancelled) {
                    setCoverImageUrl('');
                    setIsCoverLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [hasCoverSurface, shouldLoad, node?.key, rawCover, storagePath]);

    if (!node) return null;

    const isArticleNode = String(node?.type || '').trim().toUpperCase() === 'ARTICLE';
    const contentsField = showNoteContents
        ? (schemaFieldMap.get('contents') || null)
        : null;
    const noteContentsValue = contentsField
        ? resolveFieldValue(node, contentsField)
        : null;
    const noteContents = contentsField && showNoteContents
        ? formatFieldValue(noteContentsValue, contentsField, 'contents')
        : '';
    const articleSummary = isArticleNode
        ? String(node?.articleSummary || '').trim() || buildRichTextSummary(node?.article)
        : '';
    const subtitle = showSubtitle
        ? (isArticleNode
            ? articleSummary
            : (noteContents ? '' : getNodeSubtitle(node).trim()))
        : '';

    // ----- RENDER ----- //

    return (
        <div className="gallery-view-node-card-container">
            {hasCoverSurface && (
                <div
                  className={`gallery-view-node-card-cover${isCoverLoading ? ' is-loading' : ''}`}
                  ref={containerRef}
                >
                    {coverImageUrl ? (
                        <img
                          className="gallery-view-node-card-cover-image"
                          alt={node?.title || 'Cover'}
                          src={coverImageUrl}
                          draggable="false"
                        />
                    ) : (

                        <div className="gallery-view-node-card-cover-placeholder" />
                    )}
                </div>
            )}

            <div
              className="gallery-view-node-card-title"
              style={{
                    color: theme.foreground,
                }}
            >
                {node?.title || 'Untitled'}
            </div>

            {/* NOTE CONTENTS */}
            {noteContents && (
                <div
                  className="gallery-view-node-card-note-contents"
                >
                    {noteContents}
                </div>
            )}

            {/* DESCRIPTION */}
            {subtitle && (
                <div
                  className={`gallery-view-node-card-description${isArticleNode ? ' is-article-summary' : ''}`}
                >
                    {subtitle}
                </div>
            )}
            {displayFields.length > 0 && (
                <div className="gallery-view-node-card-fields">
                    {displayFields.map((field) => (
                        <div
                          className="gallery-view-node-card-field"
                          key={`gallery_node_${node?.key}_${field.fieldKey}`}
                        >
                            {!field.hideLabel && (
                                <span
                                  className="gallery-view-node-card-field-label"
                                  style={{
                                    color: theme.foregroundMuted,
                                  }}
                                >
                                    {field.label}
                                </span>
                            )}
                            <span
                              className="gallery-view-node-card-field-value"
                              style={{
                                color: theme.foreground,
                              }}
                            >
                                {field.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Dev Tools
GalleryViewNodeCard.displayName = 'GalleryViewNodeCard';

export default GalleryViewNodeCard;
